import { FC, useState } from "react";
import { Button, List, Space, Upload, message } from "antd";
import { UploadOutlined, DeleteOutlined, PaperClipOutlined } from "@ant-design/icons";
import type { RcFile } from "antd/lib/upload";

import { Document } from "../../../../types/Document";
import {
    uploadAnalysisAttachment,
    deleteAnalysisAttachment,
    getAttachmentDownloadUrl,
} from "../../../../common/api/ProjectClient";
import { handleError } from "../../../../common/ErrorHandling";

interface AttachmentsTabProps {
    projectId: number;
    analysisVersion: number;
    attachments: Document[];
    onAttachmentsChange: () => void | Promise<void>;
}

export const AttachmentsTab: FC<AttachmentsTabProps> = ({
    projectId,
    analysisVersion,
    attachments,
    onAttachmentsChange,
}) => {
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const handleUpload = async (file: RcFile) => {
        setUploading(true);
        try {
            await uploadAnalysisAttachment(projectId, analysisVersion, file);
            message.success(`${file.name} uploaded`);
            await onAttachmentsChange();
        } catch (error) {
            handleError(error);
        } finally {
            setUploading(false);
        }
        return false; // prevent antd default upload
    };

    const handleDelete = async (documentId: number) => {
        setDeletingId(documentId);
        try {
            await deleteAnalysisAttachment(projectId, analysisVersion, documentId);
            message.success("Attachment deleted");
            await onAttachmentsChange();
        } catch (error) {
            handleError(error);
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Upload beforeUpload={handleUpload} showUploadList={false} disabled={uploading}>
                <Button icon={<UploadOutlined />} loading={uploading}>
                    Upload attachment
                </Button>
            </Upload>

            {attachments.length > 0 && (
                <List
                    size="small"
                    bordered
                    dataSource={attachments}
                    renderItem={(doc) => (
                        <List.Item
                            actions={[
                                <Button
                                    key="delete"
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    loading={deletingId === doc.id}
                                    onClick={() => handleDelete(doc.id)}
                                />,
                            ]}
                        >
                            <a
                                href={getAttachmentDownloadUrl(projectId, analysisVersion, doc.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <PaperClipOutlined style={{ marginRight: 8 }} />
                                {doc.name}
                            </a>
                        </List.Item>
                    )}
                />
            )}
        </Space>
    );
};
